import { uiText } from '../i18n/uiText';
import React, { useMemo } from 'react';
import { Bot, CheckCircle2, ClipboardList, LifeBuoy, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import { useAuth } from '../store/AuthContext';

const ROLE_GUIDES = {
  admin: [
    [uiText("Проверьте доступы"), uiText("Управление → Учётные записи: создавайте пользователей, блокируйте доступ и завершайте сеансы."), '/users'],
    [uiText("Подготовьте справочники"), uiText("Добавьте компании, аудиторов и шаблоны до создания первого аудита."), '/management'],
    [uiText("Контролируйте изменения"), uiText("Журнал показывает действия, а корзина позволяет восстановить удаленные записи."), '/admin/audit-trail'],
  ],
  auditor: [
    [uiText("Откройте назначенные аудиты"), uiText("В реестре доступны все аудиты; чужие открываются только для просмотра."), '/audits'],
    [uiText("Зафиксируйте замечания"), uiText("Создавайте замечания из карточки аудита и передавайте их проверяемой стороне."), '/issues'],
    [uiText("Проверьте корректирующие действия"), uiText("Принимайте подтверждения только после проверки приложенных материалов."), '/corrective-actions'],
  ],
  auditee: [
    [uiText("Проверьте организацию"), uiText("В разделе «Моя организация» можно увидеть команду и пригласить коллег."), '/my-organization'],
    [uiText("Заполните анкету организации"), uiText("Ответьте на вопросы и приложите подтверждающие документы."), '/self-check'],
    [uiText("Следите за замечаниями"), uiText("Откройте аудит и подготовьте план корректирующих действий."), '/dashboard'],
  ],
  customer: [
    [uiText("Проверьте организацию"), uiText("В разделе «Моя организация» можно увидеть команду и пригласить коллег."), '/my-organization'],
    [uiText("Заполните анкету организации"), uiText("Ответьте на вопросы и приложите подтверждающие документы."), '/self-check'],
    [uiText("Следите за замечаниями"), uiText("Откройте аудит и подготовьте план корректирующих действий."), '/dashboard'],
  ],
  analyst: [
    [uiText("Проверьте сводку"), uiText("В сводке собраны рейтинги, риски и динамика компаний."), '/company-summary'],
  ],
};

const STATUSES = [
  [uiText("Черновик"), uiText("Данные ещё готовятся и не переданы следующему участнику.")],
  [uiText("В работе"), uiText("Ответственный уже выполняет действие.")],
  [uiText("На уточнении"), uiText("Не хватает данных или требуется корректировка.")],
  [uiText("На согласовании"), uiText("Материал передан ответственному проверяющему.")],
  [uiText("Готов к закрытию"), uiText("Обязательные этапы завершены, требуется формальное закрытие.")],
  [uiText("Завершён"), uiText("Процесс закрыт; данные доступны в истории и отчётах.")],
];

export default function HelpPage() {
  const { user } = useAuth();
  const guide = useMemo(() => ROLE_GUIDES[user?.role] || ROLE_GUIDES.auditor, [user?.role]);

  return (
    <div>
      <PageHeader icon={LifeBuoy} title={uiText("Помощь")} subtitle={uiText("Короткие маршруты по вашей роли и единые пояснения статусов.")} />

      <div className="help-layout">
        <section className="card help-card">
          <div className="card-header"><div><div className="card-title"><Sparkles size={17} />{uiText(" С чего начать")}</div><div className="workspace-muted">{uiText("Рекомендованный порядок для вашей роли")}</div></div></div>
          <div className="card-body help-steps">
            {guide.map(([title, description, to], index) => (
              <Link to={to} className="help-step" key={title}>
                <span className="help-step-number">{index + 1}</span>
                <span><b>{title}</b><small>{description}</small></span>
              </Link>
            ))}
          </div>
        </section>

        <section className="card help-card">
          <div className="card-header"><div><div className="card-title"><ClipboardList size={17} />{uiText(" Что означают статусы")}</div><div className="workspace-muted">{uiText("Общая логика движения документов и задач")}</div></div></div>
          <div className="card-body help-statuses">
            {STATUSES.map(([status, description]) => <div className="help-status" key={status}><CheckCircle2 size={16} /><div><b>{status}</b><small>{description}</small></div></div>)}
          </div>
        </section>
      </div>

      {(user?.role === 'admin' || user?.role === 'auditor') && (
        <section className="card help-ai-card">
          <Bot size={22} />
          <div><b>{uiText("Нужен ответ по документам?")}</b><p>{uiText("Локальный ИИ-ассистент ищет по общей базе аудитов и вашим личным загрузкам. Личные файлы не видны другим пользователям.")}</p></div>
          <Link className="btn btn-primary" to="/ai-assistant">{uiText("Открыть ассистента")}</Link>
        </section>
      )}

      {user?.role === 'admin' && <div className="alert"><ShieldCheck size={16} />{uiText(" Все административные изменения фиксируются в журнале действий.")}</div>}
    </div>
  );
}
